import React, { useState, useMemo, useCallback } from 'react';
import './HotelResults.css';

function HotelResults({
  hotels,
  selectedHotels,
  onSelectHotel,
  onSelectAll,
  onDeselectAll,
  onBuildComparison,
  searchParams,
  loading,
  onBackToSearch,
}) {
  const [filterRating, setFilterRating] = useState('all');
  const [sortBy, setSortBy] = useState('rating');
  const [expandedHotel, setExpandedHotel] = useState(null);

  const filteredAndSortedHotels = useMemo(() => {
    let filtered = hotels;

    if (filterRating !== 'all') {
      filtered = filtered.filter((h) => h.starRating >= parseInt(filterRating));
    }

    let sorted = [...filtered];
    switch (sortBy) {
      case 'price-asc':
        sorted.sort((a, b) => (a.lowestRate || 0) - (b.lowestRate || 0));
        break;
      case 'price-desc':
        sorted.sort((a, b) => (b.lowestRate || 0) - (a.lowestRate || 0));
        break;
      case 'rating':
        sorted.sort((a, b) => (b.starRating || 0) - (a.starRating || 0));
        break;
      case 'distance':
        sorted.sort((a, b) => (a.distance || 0) - (b.distance || 0));
        break;
      default:
        break;
    }

    return sorted;
  }, [hotels, filterRating, sortBy]);

  const handleToggleExpand = useCallback((hotelId) => {
    setExpandedHotel((prev) => (prev === hotelId ? null : hotelId));
  }, []);

  const handleQuickPick = useCallback((type) => {
    let hotelIds = [];

    if (type === 'cheapest') {
      if (filteredAndSortedHotels.length > 0) {
        hotelIds = [filteredAndSortedHotels[0].id];
      }
    } else if (type === 'highest-rated') {
      const highestRated = filteredAndSortedHotels.reduce((max, h) =>
        (h.starRating || 0) > (max.starRating || 0) ? h : max
      );
      if (highestRated) {
        hotelIds = [highestRated.id];
      }
    } else if (type === 'top-3') {
      hotelIds = filteredAndSortedHotels.slice(0, 3).map((h) => h.id);
    }

    onSelectAll(hotelIds);
  }, [filteredAndSortedHotels, onSelectAll]);

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span key={i} className={i < rating ? 'star-filled' : 'star-empty'}>
          ★
        </span>
      );
    }
    return stars;
  };

  if (loading) {
    return (
      <div className="hotel-results">
        <div className="loading-state">
          <div className="spinner-large"></div>
          <p>Searching for hotels...</p>
        </div>
      </div>
    );
  }

  if (hotels.length === 0) {
    return (
      <div className="hotel-results">
        <div className="empty-state">
          <div className="empty-icon">🏨</div>
          <h2>No hotels found</h2>
          <p>Try adjusting your search criteria or dates</p>
          <button className="button-primary" onClick={onBackToSearch}>
            ← Back to Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="hotel-results">
      <div className="results-header">
        <div className="results-title">
          <h2>Available Hotels</h2>
          <p className="results-count">
            {filteredAndSortedHotels.length} of {hotels.length} hotels
            {filterRating !== 'all' && ` (${filterRating}★+)`}
          </p>
        </div>
        <button className="button-secondary" onClick={onBackToSearch}>
          ← New Search
        </button>
      </div>

      <div className="results-controls">
        <div className="filters-section">
          <div className="filter-group">
            <label>Filter by Rating:</label>
            <div className="filter-buttons">
              {['all', '3', '4', '5'].map((rating) => (
                <button
                  key={rating}
                  className={`filter-button ${
                    filterRating === rating ? 'active' : ''
                  }`}
                  onClick={() => setFilterRating(rating)}
                >
                  {rating === 'all' ? 'All' : `${rating}★+`}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <label>Sort by:</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="rating">Star Rating</option>
              <option value="price-asc">Price (Low to High)</option>
              <option value="price-desc">Price (High to Low)</option>
              <option value="distance">Distance</option>
            </select>
          </div>
        </div>

        <div className="quick-picks-section">
          <label>Quick picks:</label>
          <div className="quick-buttons">
            <button
              className="quick-pick-button"
              onClick={() => handleQuickPick('cheapest')}
            >
              💰 Cheapest
            </button>
            <button
              className="quick-pick-button"
              onClick={() => handleQuickPick('highest-rated')}
            >
              ⭐ Highest Rated
            </button>
            <button
              className="quick-pick-button"
              onClick={() => handleQuickPick('top-3')}
            >
              🏆 Top 3
            </button>
          </div>
        </div>
      </div>

      <div className="hotels-grid">
        {filteredAndSortedHotels.map((hotel) => (
          <div key={hotel.id} className="hotel-card">
            <div className="card-header">
              <div className="card-checkbox">
                <input
                  type="checkbox"
                  id={`hotel-${hotel.id}`}
                  checked={selectedHotels.has(hotel.id)}
                  onChange={() => onSelectHotel(hotel.id)}
                />
                <label htmlFor={`hotel-${hotel.id}`}></label>
              </div>

              <div className="card-title">
                <h3>{hotel.name}</h3>
                <div className="card-rating">
                  <div className="stars">{renderStars(hotel.starRating)}</div>
                  <span className="star-count">{hotel.starRating}</span>
                </div>
              </div>

              {hotel.chainName && (
                <span className="chain-badge">{hotel.chainName}</span>
              )}
            </div>

            <div className="card-body">
              <div className="hotel-info">
                <p className="hotel-address">
                  📍 {hotel.address?.city || hotel.city}{hotel.address?.state || hotel.state ? `, ${hotel.address?.state || hotel.state}` : ''}
                </p>
                {hotel.distance && (
                  <p className="hotel-distance">
                    📏 {hotel.distance.toFixed(1)} km from center
                  </p>
                )}
              </div>

              {hotel.amenities && hotel.amenities.length > 0 && (
                <div className="amenities">
                  {hotel.amenities.slice(0, 4).map((amenity, idx) => (
                    <span key={idx} className="amenity-badge">
                      {amenity}
                    </span>
                  ))}
                  {hotel.amenities.length > 4 && (
                    <span className="amenity-more">
                      +{hotel.amenities.length - 4} more
                    </span>
                  )}
                </div>
              )}

              <div className="card-pricing">
                <div className="lowest-rate">
                  <span className="price-label">From</span>
                  <span className="price-amount">
                    ${hotel.lowestRate?.toFixed(2) || 'N/A'}
                  </span>
                  <span className="price-unit">per night</span>
                </div>
              </div>
            </div>

            <div className="card-expand">
              <button
                className="expand-button"
                onClick={() => handleToggleExpand(hotel.id)}
              >
                <span className={expandedHotel === hotel.id ? 'rotated' : ''}>
                  ▾
                </span>
                View rooms
              </button>
            </div>

            {expandedHotel === hotel.id && (
              <div className="card-rooms">
                <table className="rooms-table">
                  <thead>
                    <tr>
                      <th>Room Type</th>
                      <th>Rate/Night</th>
                      <th>Total Stay</th>
                      <th>Cancellation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hotel.rooms && hotel.rooms.length > 0 ? (
                      hotel.rooms.map((room, idx) => {
                        const nights =
                          searchParams.checkIn && searchParams.checkOut
                            ? Math.ceil(
                                (new Date(searchParams.checkOut) -
                                  new Date(searchParams.checkIn)) /
                                  (1000 * 60 * 60 * 24)
                              )
                            : 1;
                        const totalCost = (room.ratePerNight || 0) * nights;

                        return (
                          <tr key={idx}>
                            <td className="room-type">{room.roomType || room.type || 'Standard'}</td>
                            <td className="room-rate">
                              ${room.ratePerNight?.toFixed(2) || 'N/A'}
                            </td>
                            <td className="room-total">
                              ${totalCost.toFixed(2)}
                            </td>
                            <td className="room-cancel">
                              {room.cancellationPolicy || 'Standard'}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="4" className="no-rooms">
                          No rooms available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>

      {selectedHotels.size >= 2 && (
        <div className="sticky-bottom-bar">
          <div className="bar-content">
            <span className="selection-info">
              {selectedHotels.size} {selectedHotels.size === 1 ? 'hotel' : 'hotels'} selected
            </span>
            <button
              className="button-primary"
              onClick={onBuildComparison}
            >
              Build Comparative Quote →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default HotelResults;
